/**
 * Fund NAV Calculator
 *
 * For fund-of-funds clients (like FAV Funds) where each underlying fund
 * has a total NAV value, and users own a percentage based on their contributions.
 *
 * Key difference from FundInvestorCalculator:
 * - FundInvestorCalculator: capitalAccount IS the current value (TVPI is computed as output)
 * - FundNavCalculator: value = Fund NAV × User Percentage
 *
 * Formula per class:
 *   User Percentage = round(User Contributions / Total Fund Contributions, precision)
 *   Class Value = Fund NAV × User Percentage
 *   where:
 *   - Fund NAV = total fund value (stored in sharePrice field as raw dollar value)
 *   - User Contributions = sum of user's contribution events
 *   - Total Fund Contributions = sum of all fund contribution events
 *   - precision = configurable decimal places for percentage (default: 4, giving 2 decimal % like 8.03%)
 *
 * Example (FAV investor in Jana Care fund):
 *   Total Fund Contributions = $400,000
 *   Fund NAV = $220,358 (stored in sharePrice as raw dollar value)
 *   User Contributions = $32,128
 *   User Percentage = round($32,128 / $400,000, 4) = 0.0803 (8.03%)
 *   Class Value = $220,358 × 0.0803 = $17,694.75
 *
 * Total Value = Sum(all Class Values) + Distributions + Returns of Capital
 *
 * Precision Configuration:
 *   input.options.percentagePrecision - Number of decimal places for percentage (default: 4)
 *   Example: percentagePrecision=4 means 0.0803, displayed as 8.03%
 *            percentagePrecision=6 means 0.080320, displayed as 8.0320%
 *
 * TVPI Input Support:
 *   Some clients store TVPI (a multiplier like 5.52x) in the sharePrice field
 *   instead of raw Fund NAV dollars. The input assembly layer (index.js) handles
 *   the conversion: Fund NAV = TVPI × Total Fund Contributions, so this calculator
 *   always receives fundNav as a raw dollar value.
 */

const TvpiFundCalculator = {
  methodId: 'tvpi_fund',
  displayName: 'Fund NAV (Percentage of NAV)',
  requiredDataSources: ['fundNav', 'contributions', 'fundTotalContributions', 'distributions', 'returnsOfCapital'],

  // Graph configuration - 2-bar chart shows contributions vs total value
  graphConfig: {
    recommendedType: 'comparison_bars',
    timeSeriesStrategy: 'fund_capital_account',
    supportsTimeSeries: true
  },

  /**
   * Round a number to specified decimal places
   * @param {number} value - Value to round
   * @param {number} decimals - Number of decimal places
   * @returns {number} - Rounded value
   */
  roundToDecimal: function(value, decimals) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  },

  /**
   * Calculate fund value using Fund NAV and user percentage
   * @param {Object} input
   * @param {Object} input.holdings - { fundNav, contributions, fundTotalContributions, distributions, returnsOfCapital } by class
   * @param {string[]} input.shareClasses - Array of share class keys
   * @param {Object} input.options - { percentagePrecision: number } calculation options
   * @returns {Object} CalculationResult
   */
  calculate: function(input) {
    const holdings = input.holdings;
    const shareClasses = input.shareClasses || Object.keys(holdings.contributions || {});
    const options = input.options || {};

    // Percentage precision: number of decimal places for ownership percentage
    // Default to 4 (e.g., 0.0803 = 8.03%)
    // Companies often use 2-4 decimal places for percentage display
    const percentagePrecision = options.percentagePrecision !== undefined ? options.percentagePrecision : 4;

    let totalValue = 0;
    let totalContributions = 0;
    let totalDistributions = 0;
    let totalCapitalAccount = 0;
    let totalReturnsOfCapital = 0;
    const breakdown = [];
    const self = this;

    shareClasses.forEach(function(key) {
      const userContributions = holdings.contributions?.[key] || 0;
      const distributions = holdings.distributions?.[key] || 0;
      const returnsOfCapital = holdings.returnsOfCapital?.[key] || 0;

      // Fund NAV is stored in sharePrice field (raw dollar value of total fund)
      // Note: if sharePrice was originally TVPI, the input assembly converts it to
      // raw NAV before passing it here (fundNav = TVPI × fundTotalContributions)
      const fundNav = holdings.fundNav?.[key] || 0;

      // Total fund contributions (from clientData.equity[class].events)
      const fundTotalContributions = holdings.fundTotalContributions?.[key] || 0;

      // Calculate user percentage: User Contributions / Total Fund Contributions
      // Round to configured precision to match company calculation methodology
      const rawPercentage = fundTotalContributions > 0 ? userContributions / fundTotalContributions : 0;
      const userPercentage = self.roundToDecimal(rawPercentage, percentagePrecision);

      // Class value = Fund NAV × User Percentage (using rounded percentage)
      const classValue = fundNav * userPercentage;

      // User's capital account (their share of the fund's total contributions)
      const capitalAccount = fundTotalContributions * userPercentage;

      totalContributions += userContributions;
      totalDistributions += distributions;
      totalReturnsOfCapital += returnsOfCapital;
      totalCapitalAccount += capitalAccount;
      totalValue += classValue;

      // Calculate per-class TVPI: (value + distributions + RoC) / contributions
      const classRealizedValue = distributions + returnsOfCapital;
      const classTotalValue = classValue + classRealizedValue;
      const classTvpi = userContributions > 0 ? classTotalValue / userContributions : 1;

      // Only include classes with actual data
      if (userContributions > 0) {
        breakdown.push({
          classKey: key,
          className: key,
          value: classValue,
          totalValue: classTotalValue,  // value + distributions + RoC
          capitalAccount: capitalAccount,
          fundNav: fundNav,
          fundTotalContributions: fundTotalContributions,
          userPercentage: userPercentage,
          contributions: userContributions,
          distributions: distributions,
          returnsOfCapital: returnsOfCapital,
          tvpi: classTvpi  // Per-class TVPI
        });
      }
    });

    // Add realized value (distributions + RoC) to total
    const totalRealizedValue = totalDistributions + totalReturnsOfCapital;
    const totalWithRealized = totalValue + totalRealizedValue;

    // Calculate aggregate fund metrics
    const aggregateTvpi = totalContributions > 0 ? totalWithRealized / totalContributions : 1;
    const dpi = totalContributions > 0 ? totalRealizedValue / totalContributions : 0;
    const rvpi = totalContributions > 0 ? totalValue / totalContributions : 0;

    return {
      totalValue: totalWithRealized,  // Total including realized value
      totalUnrealizedValue: totalValue,  // Sum of (capitalAccount × tvpi) per class
      totalContributions: totalContributions,
      totalDistributions: totalDistributions,
      totalCapitalAccount: totalCapitalAccount,
      totalReturnsOfCapital: totalReturnsOfCapital,
      breakdown: breakdown,
      fundMetrics: {
        pic: totalContributions,
        tvpi: aggregateTvpi,
        dpi: dpi,
        rvpi: rvpi,
        moic: aggregateTvpi
      },
      metadata: {
        methodUsed: this.methodId,
        calculationDate: new Date(),
        percentagePrecision: percentagePrecision
      }
    };
  }
};

export { TvpiFundCalculator };
